import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminSidebar from "../components/AdminSidebar";
import AgentSidebar from "../components/AgentSidebar";

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const badge = (status) =>
  ({
    PUBLISHED: "bg-emerald-100 text-emerald-700",
    PENDING_REVIEW: "bg-amber-100 text-amber-700",
    PROCESSING: "bg-blue-100 text-blue-700",
    REJECTED: "bg-slate-200 text-slate-600",
    FAILED: "bg-red-100 text-red-700",
  })[status] || "bg-slate-100 text-slate-600";

function ListEditor({ label, items, onChange, placeholder }) {
  const update = (i, v) => onChange(items.map((item, idx) => (idx === i ? v : item)));
  const add = () => onChange([...items, ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">{label}</label>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={item}
              onChange={(e) => update(i, e.target.value)}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 rounded-lg border border-[#e2e8f0] outline-none text-sm"
            />
            <button type="button" onClick={() => remove(i)} className="p-2 text-red-500">
              <span className="material-symbols-outlined text-[18px]">remove_circle</span>
            </button>
          </div>
        ))}
        <button type="button" onClick={add} className="text-[#00255a] text-sm font-semibold flex items-center gap-1">
          <span className="material-symbols-outlined text-[16px]">add</span> Add
        </button>
      </div>
    </div>
  );
}

export default function InstitutionDetailPage({ isAdmin = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(isAdmin ? "adminToken" : "token");
  const headers = { Authorization: `Bearer ${token}` };
  const base = isAdmin ? "/admin/institutions" : "/agent/institutions";

  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [heroFile, setHeroFile] = useState(null);

  const load = async () => {
    setLoading(true);
    const res = await fetch(`${API_BASE}/admin/institutions/${id}`, { headers });
    const json = await res.json();
    if (json.success) {
      const i = json.data;
      setInstitution(i);
      setForm({
        name: i.name || "",
        country: i.country || "",
        description: i.description || "",
        websiteUrl: i.websiteUrl || "",
        normalFee: i.normalFee ?? "",
        discountFee: i.discountFee ?? "",
        feeCurrency: i.feeCurrency || "USD",
        globalRank: i.globalRank ?? "",
        localRank: i.localRank ?? "",
        europeRank: i.europeRank ?? "",
        highlights: i.highlights?.length ? i.highlights : [""],
        sources: i.sources?.length ? i.sources : [""],
        facts: {
          location: i.facts?.location || "",
          founded: i.facts?.founded || "",
          universityType: i.facts?.universityType || "",
          programs: i.facts?.programs || "",
        },
      });
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("country", form.country);
    fd.append("description", form.description);
    fd.append("websiteUrl", form.websiteUrl);
    fd.append("normalFee", form.normalFee);
    fd.append("discountFee", form.discountFee);
    fd.append("feeCurrency", form.feeCurrency);
    fd.append("globalRank", form.globalRank);
    fd.append("localRank", form.localRank);
    fd.append("europeRank", form.europeRank);
    fd.append("highlights", JSON.stringify(form.highlights.filter(Boolean)));
    fd.append("sources", JSON.stringify(form.sources.filter(Boolean)));
    fd.append("facts", JSON.stringify(form.facts));
    if (logoFile) fd.append("logo", logoFile);
    if (heroFile) fd.append("heroImage", heroFile);

    await fetch(`${API_BASE}/admin/institutions/${id}`, {
      method: "PATCH",
      headers,
      body: fd,
    });
    setSaving(false);
    setLogoFile(null);
    setHeroFile(null);
    load();
  };

  if (loading || !form) {
    return (
      <div className="bg-[#f7f9fb] min-h-screen font-['Plus_Jakarta_Sans']">
        {isAdmin ? <AdminSidebar /> : <AgentSidebar />}
        <main className="pt-16 md:pt-2 md:ml-64 p-8">
          <p className="text-center text-[#64748b] py-20">Loading…</p>
        </main>
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="bg-[#f7f9fb] min-h-screen font-['Plus_Jakarta_Sans']">
        {isAdmin ? <AdminSidebar /> : <AgentSidebar />}
        <main className="pt-16 md:pt-2 md:ml-64 p-8">
          <p className="text-center text-[#64748b] py-20">Institution not found.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-[#f7f9fb] min-h-screen font-['Plus_Jakarta_Sans']">
      {isAdmin ? <AdminSidebar /> : <AgentSidebar />}
      <main className="pt-16 md:pt-2 md:ml-64 p-5 md:p-8 max-w-4xl">
        <Link to={base} className="text-sm text-[#365da3] font-semibold">← Back to institutions</Link>

        <div className="flex items-center justify-between mt-3 mb-7">
          <div>
            <h1 className="text-3xl font-bold text-[#00255a]">{institution.name}</h1>
            <p className="text-[#64748b] mt-1">{institution.country}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${badge(institution.reviewStatus)}`}>
            {institution.reviewStatus.replace("_", " ")}
          </span>
        </div>

        <form onSubmit={handleSave} className="bg-white rounded-2xl border border-[#e2e8f0] p-8 space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">University Name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Country</label>
              <input
                required
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Website URL</label>
            <input
              value={form.websiteUrl}
              onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
              placeholder="https://…"
              className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Description</label>
            <textarea
              rows="4"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10 resize-none"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Logo</label>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg bg-[#f2f4f6] border border-[#e2e8f0] overflow-hidden flex items-center justify-center shrink-0">
                  {(logoFile ? URL.createObjectURL(logoFile) : institution.logoUrl) ? (
                    <img
                      src={logoFile ? URL.createObjectURL(logoFile) : institution.logoUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="material-symbols-outlined text-[#64748b]">account_balance</span>
                  )}
                </div>
                <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files[0])} className="text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Hero Image</label>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg bg-[#f2f4f6] border border-[#e2e8f0] overflow-hidden flex items-center justify-center shrink-0">
                  {(heroFile ? URL.createObjectURL(heroFile) : institution.heroImageUrl) ? (
                    <img
                      src={heroFile ? URL.createObjectURL(heroFile) : institution.heroImageUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="material-symbols-outlined text-[#64748b]">image</span>
                  )}
                </div>
                <input type="file" accept="image/*" onChange={(e) => setHeroFile(e.target.files[0])} className="text-sm" />
              </div>
            </div>
          </div>

          <div className="border-t border-[#e2e8f0] pt-6 grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Normal Fee</label>
              <input
                type="number"
                value={form.normalFee}
                onChange={(e) => setForm({ ...form, normalFee: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Discount Fee</label>
              <input
                type="number"
                value={form.discountFee}
                onChange={(e) => setForm({ ...form, discountFee: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Currency</label>
              <select
                value={form.feeCurrency}
                onChange={(e) => setForm({ ...form, feeCurrency: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
              >
                  <option>USD</option> {/* US Dollar */}
                  <option>EUR</option> {/* Euro */}
                  <option>GBP</option> {/* British Pound */}
                  <option>TRY</option> {/* Turkish Lira */}
                  <option>CHF</option> {/* Swiss Franc */}
                  <option>SEK</option> {/* Swedish Krona */}
                  <option>NOK</option> {/* Norwegian Krone */}
                  <option>DKK</option> {/* Danish Krone */}
                  <option>PLN</option> {/* Polish Zloty */}
                  <option>CZK</option> {/* Czech Koruna */}
                  <option>HUF</option> {/* Hungarian Forint */}
                  <option>RON</option> {/* Romanian Leu */}
                  <option>BGN</option> {/* Bulgarian Lev */}
                  <option>ISK</option> {/* Icelandic Krona */}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Global Rank</label>
              <input
                type="number"
                value={form.globalRank}
                onChange={(e) => setForm({ ...form, globalRank: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Local Rank</label>
              <input
                type="number"
                value={form.localRank}
                onChange={(e) => setForm({ ...form, localRank: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Europe Rank</label>
              <input
                type="number"
                value={form.europeRank}
                onChange={(e) => setForm({ ...form, europeRank: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
              />
            </div>
          </div>

          <div className="border-t border-[#e2e8f0] pt-6">
            <ListEditor
              label="Highlights / Benefits"
              items={form.highlights}
              onChange={(highlights) => setForm({ ...form, highlights })}
              placeholder="e.g. Strong graduate employment outcomes"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#44474f] uppercase mb-3">Quick Facts</label>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                ["location", "Location"],
                ["founded", "Founded"],
                ["universityType", "University Type"],
                ["programs", "Programs Offered"],
              ].map(([key, label]) => (
                <input
                  key={key}
                  value={form.facts[key]}
                  onChange={(e) => setForm({ ...form, facts: { ...form.facts, [key]: e.target.value } })}
                  placeholder={label}
                  className="px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10 text-sm"
                />
              ))}
            </div>
          </div>

          <ListEditor
            label="Source URLs"
            items={form.sources}
            onChange={(sources) => setForm({ ...form, sources })}
            placeholder="https://…"
          />

          <div className="flex gap-3 pt-2 border-t border-[#e2e8f0]">
            <button
              type="button"
              onClick={() => navigate(base)}
              className="flex-1 py-3 border border-[#e2e8f0] rounded-lg font-semibold text-[#44474f]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 text-white rounded-lg font-semibold disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)" }}
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}